import React, { type ReactNode, useEffect, useState } from "react";

import _ from "lodash";

import { gql } from "@apollo/client";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import {
  type FetchNotificationSettings as FetchNotificationSettingsQuery,
  type UpdateNotificationSettings,
  type UpdateNotificationSettingsVariables,
} from "@north-beam/nb-client-side";
import {
  type GlobalNotificationSettings,
  type ProductNotificationSettings,
} from "@north-beam/nb-common";
import { TextInput } from "@shared/text-input";

import { MmmEmailNotifications } from "./notifications/mmm-email-notifications";

function NotificationObjectEditor({
  descriptiveName,
  notifyObject,
  setNotificationObject,
  accountOwnerEmail,
}: {
  descriptiveName: string;
  notifyObject: ProductNotificationSettings;
  setNotificationObject: (arg0: ProductNotificationSettings) => void;
  accountOwnerEmail?: string;
}) {
  const [newNotificationEmail, setNewNotificationEmail] = useState<string>("");

  const handleKeyPress = (event: any) => {
    if (event.key === "Enter") {
      const emails = new Set(notifyObject.additionalRecipientEmails ?? []).add(
        newNotificationEmail,
      );

      setNotificationObject({
        ...notifyObject,
        additionalRecipientEmails: Array.from(emails),
      });
      setNewNotificationEmail("");
    }
  };

  const removeAccountOwner = () => {
    setNotificationObject({
      ...notifyObject,
      doNotNotifyAccountOwner: true,
    });
  };

  const removeEmail = (emailToRemove: string) => {
    const newEmails = (notifyObject.additionalRecipientEmails || []).filter(
      (email) => email !== emailToRemove,
    );
    setNotificationObject({
      ...notifyObject,
      additionalRecipientEmails: newEmails,
    });
  };

  const renderNoEmailRecipientsMessage = () => {
    const doNotNotifyAccountOwner = !!notifyObject.doNotNotifyAccountOwner;
    const noAdditionalRecipients =
      !notifyObject.additionalRecipientEmails ||
      notifyObject.additionalRecipientEmails.length === 0;

    if (doNotNotifyAccountOwner && noAdditionalRecipients) {
      return <p>No one from your organization receives these emails.</p>;
    }
    return null;
  };

  const shouldNotifyAccountOwner = (): boolean => {
    return !notifyObject.doNotNotifyAccountOwner;
  };

  const renderAdditonalEmailRecipients = () => {
    if (
      notifyObject.additionalRecipientEmails === undefined ||
      notifyObject.additionalRecipientEmails.length === 0
    ) {
      return null;
    }
    return notifyObject.additionalRecipientEmails.map((email) => (
      <li key={email}>
        {email}{" "}
        <i
          aria-label="Delete email"
          className="fa-regular fa-sharp fa-times"
          style={{ color: "red", cursor: "pointer" }}
          onClick={() => removeEmail(email)}
        />
      </li>
    ));
  };

  return (
    <>
      <h3 className="mt-4">
        <b>{descriptiveName}</b>
      </h3>
      <ul>
        {shouldNotifyAccountOwner() && (
          <li>
            {accountOwnerEmail}{" "}
            <i
              aria-label="Delete account owner email"
              className="fa-regular fa-sharp fa-times"
              style={{ color: "red", cursor: "pointer" }}
              onClick={removeAccountOwner}
            />
          </li>
        )}
        {renderAdditonalEmailRecipients()}
        {renderNoEmailRecipientsMessage()}
      </ul>
      <TextInput
        label="Add a new email"
        value={newNotificationEmail}
        placeholder="Email"
        id="newNotificationEmail"
        onChange={({ target }) => setNewNotificationEmail(target.value)}
        onKeyPress={handleKeyPress}
      />
    </>
  );
}

export function NotificationSettingsContainer() {
  const [synced, setSynced] = useState<boolean>(true);

  // Hack to make this weird page work with mmm without crossing
  // the simple mmm email list with the convoluted email settings
  // handled here
  const [mmmSynced, setMMMSycned] = useState<boolean>(true);
  const [mmmLoading, setMMMLoading] = useState<boolean>(true);

  const [notificationSettings, setNotificationSettings] =
    useState<GlobalNotificationSettings>({});

  const { data, loading } = useNorthbeamQuery<FetchNotificationSettingsQuery>(
    FETCH_NOTIFICATION_SETTINGS,
  );

  const [UpdateNotificationSettings] = useNorthbeamMutation<
    UpdateNotificationSettings,
    UpdateNotificationSettingsVariables
  >(UPDATE_NOTIFICATION_SETTINGS);

  useEffect(() => {
    if (data?.me?.notificationSettings) {
      setNotificationSettings(data.me.notificationSettings);
    }
  }, [data?.me?.notificationSettings]);

  const debouncedOnUpdate = React.useMemo(
    () =>
      _.debounce((newData: GlobalNotificationSettings) => {
        UpdateNotificationSettings({
          variables: { notificationSettings: newData },
        });
        setSynced(true);
      }, 500),
    [UpdateNotificationSettings, setSynced],
  );

  const setDataWithSync = React.useCallback(
    (newData) => {
      setNotificationSettings(newData);
      setSynced(false);
      debouncedOnUpdate(newData);
    },
    [setNotificationSettings, debouncedOnUpdate, setSynced],
  );

  const HACK_fakeLoadingWrapComponents = (components: ReactNode) => {
    return (
      <>
        <div className="hidden">{components}</div>
        <>Please wait...</>
      </>
    );
  };

  const hackWrapper = mmmLoading
    ? HACK_fakeLoadingWrapComponents
    : (x: ReactNode) => x;

  return (
    <div className="container pt-4">
      {!loading &&
        hackWrapper(
          <>
            <div className="row col">
              <div>
                Sync status:{" "}
                {synced && mmmSynced ? (
                  <i
                    aria-label="Data is synced"
                    className="fa-regular fa-sharp fa-check"
                  />
                ) : (
                  "..."
                )}
              </div>
            </div>
            <NotificationObjectEditor
              descriptiveName="UTM tracking emails"
              accountOwnerEmail={data?.me?.email}
              notifyObject={notificationSettings.autoNBT || {}}
              setNotificationObject={(notifyObject) =>
                setDataWithSync({
                  ...notificationSettings,
                  autoNBT: notifyObject,
                })
              }
            />
            <NotificationObjectEditor
              descriptiveName="Alerts and monitoring emails"
              accountOwnerEmail={data?.me?.email}
              notifyObject={notificationSettings.nbMonitor || {}}
              setNotificationObject={(notifyObject) =>
                setDataWithSync({
                  ...notificationSettings,
                  nbMonitor: notifyObject,
                })
              }
            />
            <MmmEmailNotifications
              setLoading={setMMMLoading}
              setSynced={setMMMSycned}
            />
          </>,
        )}
      {loading && <>Please wait...</>}
    </div>
  );
}

export const FETCH_NOTIFICATION_SETTINGS = gql`
  query FetchNotificationSettings {
    me {
      notificationSettings
      email
    }
  }
`;

export const UPDATE_NOTIFICATION_SETTINGS = gql`
  mutation UpdateNotificationSettings($notificationSettings: JSONObject!) {
    updateNotificationSettings(notificationSettings: $notificationSettings)
  }
`;
